import { useEffect, useRef, useState } from "react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { ALLERGENS, UNITS, NUTRITION_FIELDS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Sparkles, Loader2, Carrot, Upload, Download, Search } from "lucide-react";
import { toast } from "sonner";

const IMPORT_TARGETS = [
  { value: "ignore", label: "— Ignora —" },
  { value: "name", label: "Nome *" },
  { value: "category", label: "Categoria" },
  { value: "unit", label: "Unità (g/ml/pz)" },
  { value: "package_size", label: "Quantità confezione" },
  { value: "package_cost", label: "Costo confezione (€)" },
  { value: "supplier", label: "Fornitore" },
  { value: "supplier_code", label: "Codice fornitore" },
  { value: "default_waste", label: "Scarto %" },
  { value: "kcal", label: "Kcal" },
  { value: "protein", label: "Proteine" },
  { value: "carbs", label: "Carboidrati" },
  { value: "sugar", label: "Zuccheri" },
  { value: "fat", label: "Grassi" },
  { value: "saturated", label: "Saturi" },
  { value: "fiber", label: "Fibre" },
  { value: "salt", label: "Sale" },
  { value: "allergens", label: "Allergeni" },
];

const unitCostLabel = (unitCost, unit) => {
  if (unit === "g") return `${eur(unitCost * 1000)}/kg`;
  if (unit === "ml") return `${eur(unitCost * 1000)}/L`;
  return `${eur(unitCost)}/pz`;
};

const emptyForm = {
  name: "",
  category: "",
  unit: "g",
  package_size: 1000,
  package_cost: 0,
  supplier: "",
  supplier_code: "",
  default_waste: 0,
  nutrition: { kcal: 0, protein: 0, carbs: 0, sugar: 0, fat: 0, saturated: 0, fiber: 0, salt: 0 },
  allergens: [],
};

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/ingredients").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setForm({
      ...emptyForm,
      ...it,
      nutrition: { ...emptyForm.nutrition, ...(it.nutrition || {}) },
      allergens: it.allergens || [],
    });
    setEditId(it.id);
    setOpen(true);
  };

  const suggestAI = async () => {
    if (!form.name.trim()) {
      toast.error("Inserisci prima il nome dell'ingrediente");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ingredients/suggest-nutrition", { name: form.name });
      setForm((f) => ({
        ...f,
        category: data.category || f.category,
        nutrition: {
          kcal: data.kcal ?? 0,
          protein: data.protein ?? 0,
          carbs: data.carbs ?? 0,
          sugar: data.sugar ?? 0,
          fat: data.fat ?? 0,
          saturated: data.saturated ?? 0,
          fiber: data.fiber ?? 0,
          salt: data.salt ?? 0,
        },
        allergens: Array.isArray(data.allergens) ? data.allergens : f.allergens,
      }));
      toast.success("Valori nutrizionali suggeriti dall'AI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore AI");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Il nome è obbligatorio");
    setSaving(true);
    const payload = {
      ...form,
      package_size: Number(form.package_size),
      package_cost: Number(form.package_cost),
      default_waste: Number(form.default_waste) || 0,
      nutrition: Object.fromEntries(Object.entries(form.nutrition).map(([k, v]) => [k, Number(v) || 0])),
    };
    try {
      if (editId) await api.put(`/ingredients/${editId}`, payload);
      else await api.post("/ingredients", payload);
      toast.success(editId ? "Ingrediente aggiornato" : "Ingrediente aggiunto");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo ingrediente?")) return;
    await api.delete(`/ingredients/${id}`);
    toast.success("Ingrediente eliminato");
    load();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/ingredients/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "modello_ingredienti.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Errore download modello");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ingredients/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.rows.length) {
        toast.warning("Nessuna riga di dati trovata nel file");
        return;
      }
      const init = {};
      data.headers.forEach((_, i) => {
        init[i] = data.suggested_mapping[String(i)] || "ignore";
      });
      setImportData(data);
      setMapping(init);
      setImportOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura file");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    if (!Object.values(mapping).includes("name")) return toast.error("Collega una colonna al campo 'Nome'");
    setConfirming(true);
    try {
      const { data } = await api.post("/ingredients/import/confirm", { mapping, rows: importData.rows });
      const parts = [`${data.created} creati`];
      if (data.updated) parts.push(`${data.updated} aggiornati`);
      if (data.skipped) parts.push(`${data.skipped} saltati`);
      toast.success(`Import completato: ${parts.join(", ")}`);
      setImportOpen(false);
      setImportData(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore importazione");
    } finally {
      setConfirming(false);
    }
  };

  const toggleAllergen = (a) =>
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));

  const unitCost = (form.package_cost || 0) / (form.package_size || 1);

  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();

  const filtered = items.filter((it) => {
    const q = query.trim().toLowerCase();
    const matchQ =
      !q ||
      it.name.toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q) ||
      (it.supplier || "").toLowerCase().includes(q);
    const matchCat = catFilter === "all" || it.category === catFilter;
    return matchQ && matchCat;
  });

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Ingredienti</h1>
          <p className="text-muted-foreground mt-1">Le tue materie prime con costi e valori nutrizionali</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={downloadTemplate} className="gap-2" data-testid="download-template-btn">
            <Download className="w-4 h-4" /> Modello
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleImport} data-testid="import-file-input" />
          <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-2" data-testid="import-excel-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importa Excel
          </Button>
          <Button onClick={openNew} className="rounded-full gap-2" data-testid="add-ingredient-btn">
            <Plus className="w-4 h-4" /> Nuovo ingrediente
          </Button>
        </div>
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, categoria o fornitore..."
              className="pl-9"
              data-testid="ingredient-search-input"
            />
          </div>
          {categories.length > 0 && (
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-[200px]" data-testid="ingredient-category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Carrot className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">Nessun ingrediente. Aggiungi la tua prima materia prima.</p>
          <Button onClick={openNew} className="rounded-full" data-testid="empty-add-ingredient">
            <Plus className="w-4 h-4 mr-2" /> Aggiungi
          </Button>
        </Card>
      ) : (
        <Card className="border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Categoria</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Confezione</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Costo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Costo unit.</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">kcal/100</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Allergeni</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} className="hover:bg-secondary/50" data-testid={`ingredient-row-${it.id}`}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell className="text-muted-foreground">{it.category || "—"}</TableCell>
                  <TableCell className="text-right font-mono">
                    {it.package_size} {it.unit}
                  </TableCell>
                  <TableCell className="text-right font-mono">{eur(it.package_cost)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {unitCostLabel(it.unit_cost, it.unit)}
                  </TableCell>
                  <TableCell className="text-right font-mono">{it.nutrition?.kcal ?? 0}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[180px]">
                      {(it.allergens || []).slice(0, 3).map((a) => (
                        <Badge key={a} variant="secondary" className="text-[10px]">
                          {a}
                        </Badge>
                      ))}
                      {(it.allergens || []).length > 3 && (
                        <Badge variant="secondary" className="text-[10px]">
                          +{it.allergens.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(it)}
                        data-testid={`edit-ingredient-${it.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => remove(it.id)}
                        data-testid={`delete-ingredient-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Modifica ingrediente" : "Nuovo ingrediente"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Nome ingrediente</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Es. Farina 00"
                    data-testid="ingredient-name-input"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={suggestAI}
                    disabled={aiLoading}
                    className="gap-2 shrink-0"
                    data-testid="ai-suggest-btn"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    AI
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Cereali"
                  data-testid="ingredient-category-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Unità di misura</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="ingredient-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 p-4 bg-secondary/50 rounded-lg">
              <div className="space-y-1.5">
                <Label>Quantità confezione ({form.unit})</Label>
                <Input
                  type="number"
                  value={form.package_size}
                  onChange={(e) => setForm({ ...form, package_size: e.target.value })}
                  data-testid="ingredient-size-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Costo confezione (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.package_cost}
                  onChange={(e) => setForm({ ...form, package_cost: e.target.value })}
                  data-testid="ingredient-cost-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Costo unitario</Label>
                <div className="h-10 flex items-center font-mono font-semibold text-primary">
                  {unitCostLabel(unitCost, form.unit)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Fornitore</Label>
                <Input
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="Nome fornitore (facoltativo)"
                  data-testid="ingredient-supplier-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Scarto predefinito (%)</Label>
                <Input
                  type="number"
                  value={form.default_waste}
                  onChange={(e) => setForm({ ...form, default_waste: e.target.value })}
                  placeholder="Es. 20"
                  data-testid="ingredient-defaultwaste-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Valori nutrizionali (per 100 {form.unit === "pz" ? "pz" : form.unit})
              </Label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                {NUTRITION_FIELDS.map((nf) => (
                  <div key={nf.key} className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">{nf.label}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.nutrition[nf.key]}
                      onChange={(e) =>
                        setForm({ ...form, nutrition: { ...form.nutrition, [nf.key]: e.target.value } })
                      }
                      data-testid={`nutrition-${nf.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allergeni</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ALLERGENS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAllergen(a)}
                    data-testid={`allergen-${a.replace(/\s/g, "-")}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form.allergens.includes(a)
                        ? "bg-accent text-accent-foreground border-accent"
                        : "bg-transparent border-border text-muted-foreground hover:border-accent"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-ingredient-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Importa da Excel — mappa le colonne</DialogTitle>
          </DialogHeader>
          {importData && (
            <div className="space-y-5 py-2">
              <p className="text-sm text-muted-foreground">
                Collega ogni colonna del tuo file al campo corrispondente. Le righe qui sotto sono un'anteprima.
                Trovate <strong>{importData.rows.length}</strong> righe.
              </p>

              <div className="overflow-x-auto border border-border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      {importData.headers.map((h, i) => (
                        <TableHead key={i} className="min-w-[160px] align-top">
                          <div className="space-y-1.5 py-2">
                            <p className="text-xs font-semibold text-foreground truncate">{h}</p>
                            <Select value={mapping[i] || "ignore"} onValueChange={(v) => setMapping((m) => ({ ...m, [i]: v }))}>
                              <SelectTrigger className="h-8" data-testid={`map-col-${i}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {IMPORT_TARGETS.map((t) => (
                                  <SelectItem key={t.value} value={t.value}>
                                    {t.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importData.rows.slice(0, 8).map((row, ri) => (
                      <TableRow key={ri} data-testid={`import-preview-row-${ri}`}>
                        {importData.headers.map((_, ci) => (
                          <TableCell key={ci} className="text-sm font-mono truncate max-w-[200px]">
                            {row[ci] === null || row[ci] === undefined ? "—" : String(row[ci])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {importData.rows.length > 8 && (
                <p className="text-xs text-muted-foreground">…e altre {importData.rows.length - 8} righe</p>
              )}
              {!Object.values(mapping).includes("name") && (
                <p className="text-sm text-destructive">Devi collegare almeno una colonna al campo "Nome".</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)}>
              Annulla
            </Button>
            <Button onClick={confirmImport} disabled={confirming} className="rounded-full" data-testid="confirm-import-btn">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : `Importa ${importData?.rows.length || 0} righe`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
